import { ArrowRight, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AUTH_ROUTES } from "@/src/auth/constants/auth-routes";
import { POST_ROUTES } from "@/src/post/constants/post-routes";
import { CtaAnchor, CtaLink } from "../atoms/cta-link";
import { EyebrowBadge } from "../atoms/eyebrow-badge";
import { PhoneFrame } from "../atoms/phone-frame";
import { PhoneMockupScreen } from "../atoms/phone-mockup-screen";
import { SectionHeading } from "../atoms/section-heading";

interface LandingHeroSectionProps {
	communityName: string;
	communityDescription: string | null;
	appName: string | null;
	iconUrl: string | null;
	splashColor: string | null;
	primaryColor: string | null;
	isAuthenticated: boolean;
}

export function LandingHeroSection({
	communityName,
	communityDescription,
	appName,
	iconUrl,
	splashColor,
	primaryColor,
	isAuthenticated,
}: LandingHeroSectionProps) {
	const { t } = useTranslation("community");

	return (
		<section className="landing-bg-dark-hero relative overflow-hidden">
			<div className="landing-bg-grid absolute inset-0 opacity-60" aria-hidden />
			<div className="relative mx-auto grid w-full max-w-7xl gap-16 px-6 py-24 md:grid-cols-2 md:items-center md:py-32">
				<div className="landing-animate-fade-up flex flex-col gap-8">
					<SectionHeading
						eyebrow={
							<EyebrowBadge tone="white" icon={<Sparkles size={14} />}>
								{t("landing.hero.eyebrow")}
							</EyebrowBadge>
						}
						title={t("landing.hero.title", { communityName })}
						titleAccent={t("landing.hero.title_accent")}
						description={communityDescription ?? t("landing.hero.description_fallback")}
						align="left"
						tone="white"
					/>
					<div className="flex flex-wrap items-center gap-3">
						<CtaLink
							to={isAuthenticated ? POST_ROUTES.FEED : AUTH_ROUTES.AUTH}
							variant="gradient"
							size="lg"
							endContent={<ArrowRight size={18} />}
						>
							{isAuthenticated ? t("landing.hero.cta_primary_auth") : t("landing.hero.cta_primary_guest")}
						</CtaLink>
						<CtaAnchor href="#features" variant="outline-dark" size="lg">
							{t("landing.hero.cta_secondary")}
						</CtaAnchor>
					</div>
				</div>
				<div className="flex justify-center md:justify-end">
					<div className="landing-animate-glow">
						<PhoneFrame tilt="right" size="lg" label={`${communityName} mobile preview`}>
							<PhoneMockupScreen
								communityName={communityName}
								appName={appName}
								iconUrl={iconUrl}
								splashColor={splashColor}
								primaryColor={primaryColor}
							/>
						</PhoneFrame>
					</div>
				</div>
			</div>
		</section>
	);
}
